import React from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { CalendarEvent } from '../types/CalendarEvent';
import { useTranslation } from 'react-i18next';
import { AppointmentType } from '../types/AppointmentType';
import SelectCustom from '../pages/select-custom/SelectCustom';
import TimePicker from '../pages/time-picker/TimePicker';
import DurationSelect from '../pages/duration-select/DurationSelect';

interface AppointmentFormProps {
  calendarEvent: CalendarEvent;
  onChange: (field: keyof CalendarEvent, value: any) => void;
  appointmentTypes: AppointmentType[] | null;
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({ calendarEvent: appointment, onChange, appointmentTypes }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'he';

  const appointmentTypeOptions = appointmentTypes?.map((type) => ({
    value: type.name,
    label: type.name,
  }));

  const selectedValue = appointment.appointmentType?.name || '';

  return (
    <Form>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('name')}</Form.Label>
            <Form.Control
              type="text"
              value={appointment.name || ''}
              onChange={(e) => onChange('name', e.target.value)}
              className="input-bg-dark text-white border-0 shadow-sm"
            />
          </Form.Group>
        </Col>
        <Col md={3} className="mb-3">
          <Form.Group>
            <Form.Label>{t('date')}</Form.Label>
            <Form.Control
              type="date"
              value={appointment.date || ''}
              onChange={(e) => onChange('date', e.target.value)}
              className="input-bg-dark text-white border-0 shadow-sm"
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>{t('time')}</Form.Label>
            <TimePicker
              value={appointment.time || ''}
              onChange={(time: string) => onChange('time', time)}
              className="w-full"
              placeholder="Select appointment time"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('email')}</Form.Label>
            <Form.Control
              type="email"
              value={appointment.email || ''}
              onChange={(e) => onChange('email', e.target.value)}
              className="input-bg-dark text-white border-0 shadow-sm"
            />
          </Form.Group>
        </Col>

        {appointmentTypes && (
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>{t('appointment-types')}</Form.Label>
              <SelectCustom
                options={appointmentTypeOptions ?? []}
                selectedValue={selectedValue}
                onChange={(value) => {
                  const selected = appointmentTypes?.find((t) => t.name === value);
                  if (selected) {
                    onChange('appointmentType', selected);
                    if (selected.durationMinutes) {
                      onChange('durationMinutes', selected.durationMinutes);
                    }
                  }
                }}
                width="100%"
              />
            </Form.Group>
          </Col>
        )}
        <Col md={appointmentTypes ? 3 : 6}>
          <Form.Group>
            <Form.Label>{t('duration-minutes')}</Form.Label>

            {appointmentTypes ? (

              <Form.Control
                type="number"
                value={appointment.durationMinutes || ''}
                onChange={(e) => onChange('durationMinutes', parseInt(e.target.value, 10))}
                className="input-bg-dark text-white border-0 shadow-sm"
                disabled
              />

            ) : (

                <DurationSelect
                  value={appointment.durationMinutes || ''}
                  onChange={(e) => onChange('durationMinutes', parseInt(e.target.value, 10))}
                  isRtl={isRtl}
                  t={t} />

            )}


          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('phone')}</Form.Label>
            <Form.Control
              type="text"
              value={appointment.phone || ''}
              onChange={(e) => onChange('phone', e.target.value)}
              className="input-bg-dark text-white border-0 shadow-sm"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>{t('title')}</Form.Label>
            <Form.Control
              type="text"
              value={appointment.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
              className="input-bg-dark text-white border-0 shadow-sm"
              disabled
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label>{t('comments')}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={appointment.comments || ''}
          onChange={(e) => onChange('comments', e.target.value)}
          className="input-bg-dark text-white border-0 shadow-sm"
        />
      </Form.Group>
    </Form>
  );
};

export default AppointmentForm;